import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  MapearErrosServidor,
  MapearErrosServidorCampo,
} from "src/hook/Functions";
import {
  TributacaoCfopImpostoCadastroDto,
  TributacaoCfopImpostoIBSCBSCadastroSchema,
} from "../TributacaoCfopImpostoCadastro.Dto";
import {
  CstIbsCbsDto,
  ClassificacaoTributariaDto,
} from "../TributacaoCfopImpostoIBSCBS.Dto";
import {
  TributacaoCfopImpostoAlterar,
  TributacaoCfopImpostoBuscarId,
  TributacaoCfopImpostoIncluir,
  TributacaoCfopImpostoCstIbsCbsBuscar,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import TributacaoCfopImpostoIBSCBSCadastroMenu from "./TributacaoCfopImpostoIBSCBSCadastro.Menu";

const TributacaoCfopImpostoIBSCBSCadastroCode = () => {
  const [loading, setLoading] = useState(true);
  const [incluir, setIncluir] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const tributacaoCfopId = searchParams.get("tributacaocfopid");
  const type = searchParams.get("type");

  const [cstOptions, setCstOptions] = useState<CstIbsCbsDto[]>([]);
  const [classificacaoOptions, setClassificacaoOptions] = useState<
    ClassificacaoTributariaDto[]
  >([]);
  const [selectedCst, setSelectedCst] = useState<CstIbsCbsDto | undefined>(
    undefined
  );

  const menu = TributacaoCfopImpostoIBSCBSCadastroMenu({ id, setLoading });

  const form = useForm<TributacaoCfopImpostoCadastroDto>({
    resolver: yupResolver(TributacaoCfopImpostoIBSCBSCadastroSchema),
    defaultValues: {
      tributacaoCFOPId: Number(tributacaoCfopId),
      tipoImposto: Number(type),
    },
  });

  useEffect(() => {
    const controller = new AbortController();
    const Load = async () => {
      try {
        const cstResponse = await TributacaoCfopImpostoCstIbsCbsBuscar();
        if (cstResponse.data.success) {
          setCstOptions(cstResponse.data.data);
        }

        if (id) {
          setLoading(true);
          const response = await TributacaoCfopImpostoBuscarId(id);
          if (response.data.success) {
            const data =
              response.data.data as TributacaoCfopImpostoCadastroDto;
            const cst = cstResponse.data.data.find(
              (c: CstIbsCbsDto) => c.cst === String(data.cst).padStart(3, "0")
            );
            if (cst) {
              setSelectedCst(cst);
              setClassificacaoOptions(cst.classificacoesTributarias);
              data.cst = cst.id;
            }

            setTimeout(() => {
              form.reset(data);
            }, 0);

            setIncluir(false);
          } else {
            showToast({
              message: "Ocorreu erro ao carregar Imposto IBS/CBS",
              title: "Erro",
              variant: "Danger",
            });
          }
        } else {
          setIncluir(true);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    Load();
    return () => {
      controller.abort();
    };
  }, [id]);

  const watchCst = form.watch("cst");
  const watchClassTrib = form.watch("classificacoesTributarias");

  useEffect(() => {
    if (cstOptions.length > 0 && watchCst) {
      const cst = cstOptions.find((c) => c.id === watchCst);
      if (cst && selectedCst?.id !== watchCst) {
        setSelectedCst(cst);
        setClassificacaoOptions(cst.classificacoesTributarias);
      }
      form.setValue("cst", watchCst);
    }
  }, [cstOptions, watchCst, selectedCst, form]);

  useEffect(() => {
    if (classificacaoOptions.length > 0 && watchClassTrib) {
      form.setValue("classificacoesTributarias", watchClassTrib);
    }
  }, [classificacaoOptions, watchClassTrib, form]);

  const handleCstChange = (cstId: number) => {
    const cst = cstOptions.find((c) => c.id === cstId);
    setSelectedCst(cst);
    setClassificacaoOptions(cst?.classificacoesTributarias || []);
    form.setValue("cst", cstId);
    form.setValue("classificacoesTributarias", null);
  };

  const handleClassificacaoChange = (cClassTrib: string) => {
    form.setValue("classificacoesTributarias", cClassTrib);
  };

  const onSubmit = async (
    data: TributacaoCfopImpostoCadastroDto,
    e: any
  ) => {
    e.preventDefault();
    setLoading(true);

    const cst = cstOptions.find((c) => c.id === data.cst);
    if (cst) {
      data.cst = Number(cst.cst);
    }

    if (incluir === true) {
      TributacaoCfopImpostoIncluir(data)
        .then((response) => {
          if (response.data.success) {
            navigate(
              `/fiscal/tributacao/cfop/cadastro?id=${tributacaoCfopId}`
            );
          } else {
            MapearErrosServidorCampo(
              response.data.notifications,
              form.setError
            );
            MapearErrosServidor(response.data.notifications, showToast);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      data.id = Number(id);
      TributacaoCfopImpostoAlterar(data)
        .then((response) => {
          if (response.data.success) {
            navigate(
              `/fiscal/tributacao/cfop/cadastro?id=${tributacaoCfopId}`
            );
          } else {
            MapearErrosServidorCampo(
              response.data.notifications,
              form.setError
            );
            MapearErrosServidor(response.data.notifications, showToast);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return {
    loading,
    onSubmit,
    form,
    menu,
    type,
    cstOptions,
    classificacaoOptions,
    selectedCst,
    handleCstChange,
    handleClassificacaoChange,
  };
};
export default TributacaoCfopImpostoIBSCBSCadastroCode;
